import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BarChart3,
  Bot,
  CalendarDays,
  ChevronDown,
  Clock,
  Database,
  DollarSign,
  FileText,
  PhoneCall,
  PhoneMissed,
  Radio,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  UserCheck,
  UserX,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import TrendChart from '@/components/asistan/TrendChart'
import FilterBar from '@/components/asistan/FilterBar'
import {
  EMPTY_FILTERS,
  FILTER_KEYS,
  filtersToQuery,
  filtersFromQuery,
  formatEpochMs,
  formatMinor,
  formatMinutes,
  formatDuration,
  formatSecondsShort,
  formatTry,
  formatUsd,
  type Filters,
  type InsightsCallRow,
  type InsightsData,
  type InsightsPackRow,
  type InsightsTab,
  type InsightsUserRow,
} from '@/lib/asistan'

/* ---------------------------------------------------------------- small parts */

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  tone = 'indigo',
  hint,
}: {
  label: string
  value: string | number
  sub?: string
  icon: LucideIcon
  tone?: 'indigo' | 'green' | 'amber' | 'red' | 'gray'
  hint?: string
}) {
  const tones: Record<string, string> = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    gray: 'bg-gray-100 text-gray-500',
  }

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg" title={hint}>
      <div className="p-5">
        <div className="flex items-center">
          <div className={cn('flex-shrink-0 rounded-md p-3', tones[tone])}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
              <dd className="text-2xl font-semibold text-gray-900">{value}</dd>
              {sub && <dd className="text-xs text-gray-400 mt-0.5">{sub}</dd>}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}

function Card({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-4 sm:px-6 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-medium text-gray-900">{title}</h3>
        {action}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  )
}

function EmptyRow({ colSpan, label }: { colSpan: number; label: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-8 text-center text-sm text-gray-400">
        {label}
      </td>
    </tr>
  )
}

function Pill({ children, tone = 'gray' }: { children: ReactNode; tone?: 'gray' | 'green' | 'red' | 'amber' | 'indigo' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
    amber: 'bg-amber-100 text-amber-800',
    indigo: 'bg-indigo-100 text-indigo-800',
  }
  return <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', tones[tone])}>{children}</span>
}

// A money cell says which currency it is every time, because the two currencies are
// never summed anywhere in this dashboard.
function Money({ minor, currency, muted }: { minor: number; currency: string; muted?: boolean }) {
  if (!minor) return <span className={cn('text-sm', muted ? 'text-gray-300' : 'text-gray-400')}>—</span>
  const isTry = currency.toLowerCase() === 'try'
  return <span className={cn('text-sm font-medium', isTry ? 'text-emerald-700' : 'text-emerald-600')}>{formatMinor(minor, currency)}</span>
}

interface SortState {
  key: string
  dir: 'asc' | 'desc'
}

function Th({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
  hint,
}: {
  label: string
  sortKey?: string
  sort?: SortState
  onSort?: (key: string) => void
  align?: 'left' | 'right'
  hint?: string
}) {
  const active = sortKey && sort?.key === sortKey
  return (
    <th
      scope="col"
      title={hint}
      className={cn(
        'px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {sortKey && onSort ? (
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className={cn('inline-flex items-center gap-1 hover:text-gray-900', active && 'text-gray-900')}
        >
          {label}
          {active ? sort?.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" /> : <ChevronDown className="h-3 w-3 opacity-30" />}
        </button>
      ) : (
        label
      )}
    </th>
  )
}

// Sorting the rows already loaded keeps the endpoint simple, and the row cap is
// what keeps "already loaded" a bounded set.
function useSortedRows<T extends Record<string, unknown>>(rows: T[], initial: SortState) {
  const [sort, setSort] = useState<SortState>(initial)
  const onSort = useCallback(
    (key: string) => setSort((current) => ({ key, dir: current.key === key && current.dir === 'desc' ? 'asc' : 'desc' })),
    [],
  )
  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = a[sort.key]
      const bv = b[sort.key]
      if (av === bv) return 0
      if (av === null || av === undefined) return 1
      if (bv === null || bv === undefined) return -1
      const result = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sort.dir === 'asc' ? result : -result
    })
    return copy
  }, [rows, sort])
  return { sorted, sort, onSort }
}

function TruncatedNotice({ shown, limit, onRaise }: { shown: number; limit: number; onRaise: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
      <AlertTriangle className="h-3.5 w-3.5" />
      Showing the first {shown} of at least {shown + 1} matching rows (cap {limit}). Totals above cover every match; the rows below do not.
      <button type="button" onClick={onRaise} className="font-medium underline hover:no-underline">
        raise the cap
      </button>
    </div>
  )
}

/* ------------------------------------------------------------------ KPI rows */

function KpiRow({ data }: { data: InsightsData }) {
  const { summary, config, tab } = data
  const usdRevenue = summary.revenueUsdMinor / 100
  const tryRevenue = summary.revenueTryMinor / 100
  // Margin is stated in USD so the two currencies can be compared at all; the rate
  // is printed on the card so nobody has to guess which one was used.
  const tryAsUsd = tryRevenue / config.tryPerUsd
  const totalUsd = usdRevenue + tryAsUsd
  const marginUsd = totalUsd - summary.costUsd
  const marginPct = totalUsd > 0 ? (marginUsd / totalUsd) * 100 : 0

  if (tab === 'overview') {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Registered users" value={summary.users.toLocaleString()} sub={`${summary.callers} have called`} icon={Users} />
        <StatCard
          label="Billed minutes"
          value={formatSecondsShort(summary.billedSeconds).replace('h', 'h').replace('m', 'm')}
          sub={`${formatMinutes(summary.billedMinutes)} minutes · Gemini basis`}
          icon={Clock}
          tone="amber"
        />
        <StatCard label="Assistants" value={summary.assistants.toLocaleString()} sub={`${summary.activeAssistants} active`} icon={Bot} />
        <StatCard
          label="Live calls now"
          value={summary.liveCalls}
          sub={`${summary.calls.toLocaleString()} calls recorded`}
          icon={PhoneCall}
          tone={summary.liveCalls > 0 ? 'red' : 'gray'}
        />
      </div>
    )
  }

  if (tab === 'users') {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Users matching" value={summary.users.toLocaleString()} sub={`${summary.callers} have billed minutes`} icon={Users} />
        <StatCard
          label="Never called"
          value={(summary.users - summary.callers).toLocaleString()}
          sub="signed up, no billed minutes"
          icon={UserX}
          tone="amber"
          hint="Accounts with used_seconds = 0 — filter Account state: Never called"
        />
        <StatCard
          label="Minutes billed"
          value={formatMinutes(summary.billedMinutes)}
          sub={formatSecondsShort(summary.billedSeconds)}
          icon={Clock}
          tone="indigo"
        />
        <StatCard
          label="Our cost"
          value={formatUsd(summary.costUsd)}
          sub={`${formatTry(summary.costTry)} · $${config.geminiUsdPerMinute}/min`}
          icon={DollarSign}
          tone="red"
          hint="Billed seconds x the Gemini Live rate — the only real per-call variable cost"
        />
      </div>
    )
  }

  if (tab === 'calls') {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Calls matching" value={summary.calls.toLocaleString()} sub={`${summary.liveCalls} live right now`} icon={PhoneCall} />
        <StatCard
          label="Talk time"
          value={formatMinutes(summary.callMinutes) + ' min'}
          sub={`${formatSecondsShort(summary.callSeconds)} logged`}
          icon={Clock}
          tone="indigo"
        />
        <StatCard
          label="Assistants involved"
          value={summary.assistants.toLocaleString()}
          sub={`${summary.activeAssistants} active`}
          icon={Bot}
        />
        <StatCard
          label="Average call"
          value={summary.calls > 0 ? `${Math.round(summary.callSeconds / summary.calls)}s` : '—'}
          sub={`${summary.callers} users calling`}
          icon={Activity}
          tone="amber"
        />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label="Collected (USD)"
        value={formatUsd(usdRevenue)}
        sub={`${summary.revenueTryMinor > 0 ? formatTry(tryRevenue) + ' also collected' : 'no TRY sales'}`}
        icon={Wallet}
        tone="green"
        hint="payments.status = 'paid', summed per currency and never converted into each other"
      />
      <StatCard
        label="Our cost"
        value={formatUsd(summary.costUsd)}
        sub={`${formatTry(summary.costTry)} · ${formatMinutes(summary.billedMinutes)} min billed`}
        icon={TrendingDown}
        tone="red"
        hint="Lifetime billed seconds x the Gemini Live rate"
      />
      <StatCard
        label="Margin so far"
        value={totalUsd > 0 ? `${marginPct.toFixed(0)}%` : '—'}
        sub={totalUsd > 0 ? `${formatUsd(marginUsd)} at ${config.tryPerUsd} TRY/USD` : 'no sales yet'}
        icon={marginUsd >= 0 ? TrendingUp : TrendingDown}
        tone={marginUsd >= 0 ? 'green' : 'red'}
      />
      <StatCard
        label="Unbilled balance"
        value={formatSecondsShort(summary.remainingSeconds)}
        sub={`${formatMinutes(summary.remainingSeconds / 60)} min of prepaid calls owed`}
        icon={Database}
        tone="amber"
        hint="balance_seconds across all users: minutes already paid for that still have to be delivered"
      />
    </div>
  )
}

/* ------------------------------------------------------------------- tables */

function UsersTable({
  data,
  onRaise,
}: {
  data: InsightsData
  onRaise: () => void
}) {
  // Revenue is per user on this endpoint already, so the rows sort and render as they
  // arrive; a user who never paid simply carries zero.
  const { sorted, sort, onSort } = useSortedRows((data.users ?? []) as unknown as Record<string, unknown>[], {
    key: 'created_at',
    dir: 'desc',
  }) as unknown as { sorted: InsightsUserRow[]; sort: SortState; onSort: (key: string) => void }
  const limit = data.filters.limit

  return (
    <>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <Th label="Email" sortKey="email" sort={sort} onSort={onSort} />
            <Th label="Name" sortKey="name" sort={sort} onSort={onSort} />
            <Th label="Type" sortKey="account_type" sort={sort} onSort={onSort} />
            <Th label="Signed up" sortKey="created_at" sort={sort} onSort={onSort} />
            <Th label="Asst." sortKey="assistants" sort={sort} onSort={onSort} align="right" hint="Assistants on the account" />
            <Th
              label="Billed min"
              sortKey="used_seconds"
              sort={sort}
              onSort={onSort}
              align="right"
              hint="used_seconds / 60 — what the product charges the user, and what the Gemini cost is calculated from"
            />
            <Th label="Call min" sortKey="call_seconds" sort={sort} onSort={onSort} align="right" hint="Sum of calls.duration_sec — what the call log recorded" />
            <Th label="Ans/Miss" align="right" hint="Answered / missed calls" />
            <Th label="Balance" sortKey="balance_seconds" sort={sort} onSort={onSort} align="right" hint="Prepaid minutes still to be delivered" />
            <Th label="Paid USD" sortKey="revenue_usd_minor" sort={sort} onSort={onSort} align="right" />
            <Th label="Paid TRY" sortKey="revenue_try_minor" sort={sort} onSort={onSort} align="right" />
            <Th label="Cost" sortKey="cost_usd" sort={sort} onSort={onSort} align="right" hint="Gemini cost of this user's billed minutes" />
            <Th label="Last paid" sortKey="last_payment_at" sort={sort} onSort={onSort} />
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sorted.length === 0 ? (
            <EmptyRow colSpan={13} label="No users match these filters." />
          ) : (
            sorted.map((user) => (
              <tr key={user.id} className={cn('hover:bg-gray-50', user.used_seconds === 0 && 'text-gray-400')}>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  <span className="inline-flex items-center gap-2">
                    {user.email}
                    {user.deleted_at != null && <Pill tone="red">deleting</Pill>}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{user.name || '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <Pill tone={user.account_type === 'business' ? 'indigo' : 'gray'}>{user.account_type}</Pill>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatEpochMs(user.created_at)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                  {user.assistants}
                  {user.active_assistants !== user.assistants && <span className="text-gray-400"> ({user.active_assistants} on)</span>}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">{formatMinutes(user.used_minutes)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">{formatMinutes(user.call_minutes)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                  {user.answered_calls} / {user.missed_calls}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">{formatMinutes(user.balance_seconds / 60)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <Money minor={user.revenue_usd_minor} currency="usd" />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <Money minor={user.revenue_try_minor} currency="try" />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600">{user.used_seconds > 0 ? formatUsd(user.cost_usd) : '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatEpochMs(user.last_payment_at)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {data.truncated && <TruncatedNotice shown={sorted.length} limit={limit} onRaise={onRaise} />}
    </>
  )
}

function CallsTable({ data, onRaise }: { data: InsightsData; onRaise: () => void }) {
  const { sorted, sort, onSort } = useSortedRows((data.calls ?? []) as unknown as Record<string, unknown>[], {
    key: 'started_at',
    dir: 'desc',
  }) as unknown as { sorted: InsightsCallRow[]; sort: SortState; onSort: (key: string) => void }
  const limit = data.filters.limit

  return (
    <>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <Th label="Started" sortKey="started_at" sort={sort} onSort={onSort} />
            <Th label="User" sortKey="email" sort={sort} onSort={onSort} />
            <Th label="Caller" sortKey="caller_phone" sort={sort} onSort={onSort} />
            <Th label="Assistant" sortKey="assistant" sort={sort} onSort={onSort} />
            <Th label="Mode" sortKey="mode" sort={sort} onSort={onSort} />
            <Th label="Outcome" sortKey="status" sort={sort} onSort={onSort} />
            <Th label="Duration" sortKey="duration_sec" sort={sort} onSort={onSort} align="right" hint="Exact call length from calls.duration_sec, shown to the second" />
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sorted.length === 0 ? (
            <EmptyRow colSpan={7} label="No calls match these filters." />
          ) : (
            sorted.map((call) => (
              <tr key={call.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatEpochMs(call.started_at)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{call.email}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{call.caller_phone ?? '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{call.assistant}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <Pill tone={call.mode === 'ai' ? 'indigo' : 'amber'}>{call.mode === 'ai' ? 'AI' : 'rang owner'}</Pill>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <Pill tone={call.status === 'answered' ? 'green' : 'red'}>{call.status}</Pill>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">{formatDuration(call.duration_sec)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {data.truncated && <TruncatedNotice shown={sorted.length} limit={limit} onRaise={onRaise} />}
    </>
  )
}

function ExpensesTables({ data, onRaise }: { data: InsightsData; onRaise: () => void }) {
  const { summary, config } = data
  const packs = data.packs ?? []
  const payers = (data.payers ?? []) as unknown as Record<string, unknown>[]
  const { sorted, sort, onSort } = useSortedRows(payers, { key: 'last_paid_at', dir: 'desc' })
  const usdRevenue = summary.revenueUsdMinor / 100
  const tryRevenue = summary.revenueTryMinor / 100

  return (
    <div className="space-y-8">
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600 shadow-sm">
        <p className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
          <span>
            This tab reports <strong>lifetime</strong> totals on purpose. Filters like the signup window would otherwise report one month of revenue
            against the lifetime usage of whoever happened to sign up inside it. Minutes are sold as prepaid packs, so{' '}
            <strong>{formatUsd(usdRevenue + tryRevenue / config.tryPerUsd)}</strong> collected and{' '}
            <strong>{formatUsd(summary.costUsd)}</strong> of Gemini cost are on different clocks — the margin below is what has been earned and spent
            to date, not a monthly profit.
          </span>
        </p>
      </div>

      <Card
        title="Where the money came from"
        action={<span className="text-xs text-gray-500">paid payments, grouped by pack · per currency</span>}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <Th label="Pack" />
              <Th label="Currency" />
              <Th label="Payments" align="right" />
              <Th label="Minutes" align="right" />
              <Th label="SMS" align="right" />
              <Th label="Collected" align="right" />
              <Th label="Last paid" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {packs.length === 0 ? (
              <EmptyRow colSpan={7} label="No paid payments recorded yet." />
            ) : (
              packs.map((pack: InsightsPackRow) => (
                <tr key={`${pack.pack_id}-${pack.currency}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{pack.pack_id}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm uppercase text-gray-500">{pack.currency}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">{pack.payments}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">{pack.minutes ? pack.minutes.toLocaleString() : '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">{pack.sms_count ? pack.sms_count.toLocaleString() : '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <Money minor={pack.amount_minor} currency={pack.currency} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatEpochMs(pack.last_paid_at)}</td>
                </tr>
              ))
            )}
          </tbody>
          {packs.length > 0 && (
            <tfoot className="bg-gray-50">
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700" colSpan={5}>
                  Total collected
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                  {usdRevenue > 0 && <span>{formatUsd(usdRevenue)}</span>}
                  {usdRevenue > 0 && tryRevenue > 0 && <span className="text-gray-400"> + </span>}
                  {tryRevenue > 0 && <span>{formatTry(tryRevenue)}</span>}
                  {usdRevenue === 0 && tryRevenue === 0 && <span className="text-gray-400">—</span>}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </Card>

      <Card
        title="Revenue by user"
        action={<span className="text-xs text-gray-500">what each account has actually paid · both currencies</span>}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <Th label="Email" sortKey="email" sort={sort} onSort={onSort} />
              <Th label="Name" sortKey="name" sort={sort} onSort={onSort} />
              <Th label="Paid USD" sortKey="revenue_usd_minor" sort={sort} onSort={onSort} align="right" />
              <Th label="Paid TRY" sortKey="revenue_try_minor" sort={sort} onSort={onSort} align="right" />
              <Th label="Minutes bought" sortKey="minutes_purchased" sort={sort} onSort={onSort} align="right" />
              <Th label="SMS bought" sortKey="sms_purchased" sort={sort} onSort={onSort} align="right" />
              <Th label="Payments" sortKey="payments" sort={sort} onSort={onSort} align="right" />
              <Th label="Last paid" sortKey="last_paid_at" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sorted.length === 0 ? (
              <EmptyRow colSpan={8} label="Nobody has paid yet." />
            ) : (
              sorted.map((payer) => (
                <tr key={String(payer.id)} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{String(payer.email)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{(payer.name as string) || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <Money minor={Number(payer.revenue_usd_minor)} currency="usd" />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <Money minor={Number(payer.revenue_try_minor)} currency="try" />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">{Number(payer.minutes_purchased).toLocaleString()}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                    {Number(payer.sms_purchased) ? Number(payer.sms_purchased).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">{Number(payer.payments)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatEpochMs(Number(payer.last_paid_at))}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {data.truncatedPayers && <TruncatedNotice shown={sorted.length} limit={data.filters.limit} onRaise={onRaise} />}
      </Card>

      <Card
        title="Biggest assistant contexts"
        action={<span className="text-xs text-gray-500">an assistant's context is re-sent to the model on every call</span>}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <Th label="Assistant" />
              <Th label="Owner" />
              <Th label="State" />
              <Th label="Content chars" align="right" hint="The AI persona/instructions — sent to the live model with every call" />
              <Th label="Data chars" align="right" hint="Business data, queried by the cheap model on demand" />
              <Th label="Total" align="right" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(data.contexts ?? []).length === 0 ? (
              <EmptyRow colSpan={6} label="No assistants yet." />
            ) : (
              (data.contexts ?? []).map((row, i) => (
                <tr key={`${row.email}-${row.name}-${i}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{row.name ?? '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{row.email ?? '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <Pill tone={row.active ? 'green' : 'gray'}>{row.active ? 'active' : 'off'}</Pill>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">{row.content_chars.toLocaleString()}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">{row.data_chars.toLocaleString()}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">{row.total_chars.toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

/* ------------------------------------------------------------------ health */

function HealthPanels({ data }: { data: HealthData | null }) {
  const gateway = data?.gateway
  const content = data?.content
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <Card title="SIP gateway health">
        {!gateway ? (
          <div className="px-6 py-6 text-sm text-gray-400">No gateway heartbeat record found.</div>
        ) : (
          <div className="px-6 py-4 space-y-3">
            <Pill tone={gateway.registered === 1 ? 'green' : 'red'}>
              {gateway.registered === 1 ? 'Registered (NetGSM REGISTER OK)' : 'Not registered'}
            </Pill>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last beat</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatEpochMs(gateway.last_beat)}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last call</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatEpochMs(gateway.last_call_at)}</dd>
              </div>
            </dl>
          </div>
        )}
      </Card>
      <Card title="Content volume">
        <div className="px-6 py-4">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-4">
              <dt className="flex items-center text-sm text-gray-500">
                <FileText className="h-4 w-4 mr-2" /> Notes
              </dt>
              <dd className="mt-1 text-2xl font-semibold text-gray-900">{(content?.notes ?? 0).toLocaleString()}</dd>
            </div>
            <div className="rounded-lg bg-gray-50 p-4">
              <dt className="flex items-center text-sm text-gray-500">
                <Activity className="h-4 w-4 mr-2" /> Transcripts
              </dt>
              <dd className="mt-1 text-2xl font-semibold text-gray-900">{(content?.transcripts ?? 0).toLocaleString()}</dd>
            </div>
            <div className="rounded-lg bg-gray-50 p-4">
              <dt className="flex items-center text-sm text-gray-500">
                <CalendarDays className="h-4 w-4 mr-2" /> Appointments
              </dt>
              <dd className="mt-1 text-2xl font-semibold text-gray-900">{(content?.appointments ?? 0).toLocaleString()}</dd>
            </div>
          </dl>
        </div>
      </Card>
    </div>
  )
}

interface HealthData {
  gateway: { registered: number | null; last_beat: number | null; last_call_at: number | null } | null
  content: { notes: number; transcripts: number; appointments: number } | null
}

/* ------------------------------------------------------------------- page */

const TABS: { id: InsightsTab; label: string; icon: LucideIcon }[] = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'calls', label: 'Calls', icon: PhoneCall },
  { id: 'expenses', label: 'Expenses', icon: Wallet },
]

export default function AsistanPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab')
  const tab: InsightsTab = (TABS.find((t) => t.id === rawTab)?.id ?? 'users') as InsightsTab

  const [filters, setFilters] = useState<Filters>(() => filtersFromQuery(window.location.search))
  const [applied, setApplied] = useState<Filters>(filters)
  const [data, setData] = useState<InsightsData | null>(null)
  const [health, setHealth] = useState<HealthData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const query = filtersToQuery(applied, tab)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(`/api/project/asistan-insights?${query}`)
        if (!response.ok) throw new Error(`Request failed (${response.status})`)
        const result = (await response.json()) as InsightsData
        if (cancelled) return
        setData(result)
        setLastUpdated(new Date())
      } catch (err) {
        if (!cancelled) setError((err as Error).message)
      } finally {
        if (!cancelled) setLoading(false)
        setRefreshing(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [query])

  // The health panels still come from the original endpoint, which already returns
  // them; this is the one thing the old dashboard did that is worth keeping.
  useEffect(() => {
    if (tab !== 'overview') return
    let cancelled = false
    fetch('/api/project/asistan')
      .then((response) => (response.ok ? (response.json() as Promise<{ health?: HealthData }>) : null))
      .then((result) => {
        if (!cancelled && result) setHealth({ gateway: result.health?.gateway ?? null, content: result.health?.content ?? null })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [tab, lastUpdated])

  // Typing should not fire a request per keystroke. The endpoint is cheap, but a
  // list that repaints out from under a half-typed address is not.
  const searchTimer = useRef<number | null>(null)
  const onSearch = useCallback((key: 'email' | 'assistant', value: string) => {
    setFilters((current) => ({ ...current, [key]: value }))
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(() => setApplied((current) => ({ ...current, [key]: value })), 400)
  }, [])

  const patch = useCallback((next: Partial<Filters>) => {
    setFilters((current) => ({ ...current, ...next }))
  }, [])

  const onApply = useCallback(() => setApplied(filters), [])

  const onReset = useCallback(() => {
    setFilters(EMPTY_FILTERS)
    setApplied(EMPTY_FILTERS)
  }, [])

  const onRaise = useCallback(() => {
    setFilters((current) => {
      const merged = { ...current, limit: '1000' }
      setApplied(merged)
      return merged
    })
  }, [])

  const setTab = useCallback(
    (next: InsightsTab) => {
      const params = new URLSearchParams(window.location.search)
      params.set('tab', next)
      setSearchParams(params, { replace: true })
    },
    [setSearchParams],
  )

  const activeCount = useMemo(
    () => FILTER_KEYS.filter((key) => applied[key] && applied[key] !== EMPTY_FILTERS[key]).length,
    [applied],
  )

  const showChart = tab === 'overview' || (tab === 'expenses' && (data?.series?.length ?? 0) > 0)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Asistan</h1>
          <p className="mt-1 text-sm text-gray-500">
            Users, calls and spend{lastUpdated && <> · updated {lastUpdated.toLocaleTimeString()}</>}
            {data?.scope === 'lifetime' && <> · expenses are lifetime totals</>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg bg-gray-100 p-1">
            {TABS.map((entry) => {
              const Icon = entry.icon
              return (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => setTab(entry.id)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    tab === entry.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {entry.label}
                </button>
              )
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              setRefreshing(true)
              setApplied((current) => ({ ...current }))
            }}
            disabled={refreshing || loading}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', (refreshing || loading) && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      <FilterBar
        tab={tab}
        filters={filters}
        options={data?.options}
        onPatch={patch}
        onApply={onApply}
        onReset={onReset}
        onSearch={onSearch}
        loading={loading}
        activeCount={activeCount}
      />

      {error && (
        <div className="rounded-md bg-red-50 p-4 text-red-700">
          <p>Error: {error}</p>
          <button
            type="button"
            onClick={() => setApplied((current) => ({ ...current }))}
            className="mt-3 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-500"
          >
            Retry
          </button>
        </div>
      )}

      {loading && !data ? (
        <div className="flex min-h-[300px] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      ) : data ? (
        <>
          <KpiRow data={data} />

          {showChart && data.series && (
            <Card
              title="Signups and billed minutes by month"
              action={<span className="text-xs text-gray-500">{data.series.length} days of history</span>}
            >
              <TrendChart series={data.series} />
            </Card>
          )}

          {tab === 'overview' && <HealthPanels data={health} />}

          {tab === 'users' && (
            <Card
              title="Users"
              action={
                <span className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <UserCheck className="h-3.5 w-3.5 text-green-600" />
                    {data.summary.callers} calling
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    {data.summary.users - data.summary.callers} never called
                  </span>
                  <span className="hidden sm:inline">click a column to sort</span>
                </span>
              }
            >
              <UsersTable data={data} onRaise={onRaise} />
            </Card>
          )}

          {tab === 'calls' && (
            <Card
              title="Calls"
              action={
                <span className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <PhoneCall className="h-3.5 w-3.5 text-green-600" />
                    {data.summary.calls} calls
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <PhoneMissed className="h-3.5 w-3.5 text-red-500" />
                    {data.summary.liveCalls} live
                  </span>
                </span>
              }
            >
              <CallsTable data={data} onRaise={onRaise} />
            </Card>
          )}

          {tab === 'expenses' && <ExpensesTables data={data} onRaise={onRaise} />}

          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
            <span className="inline-flex items-center gap-2">
              <Radio className="h-3.5 w-3.5" />
              Timestamps are your local timezone (converted from epoch milliseconds).
            </span>
            <span className="inline-flex items-center gap-2">
              <TrendingUp className="h-3.5 w-3.5" />
              Cost uses {formatUsd(data.config.geminiUsdPerMinute)} per billed minute and {data.config.tryPerUsd} TRY per USD.
            </span>
          </div>
        </>
      ) : null}
    </div>
  )
}
